import { forwardRef } from "react";

const fmtD = (d) => `${d.slice(8)}/${d.slice(5, 7)}`;
const fmtLong = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "");
// 6 days per row; a single leftover day (31st) joins the last row instead of a lonely row.
const remarkRows = (days) => {
  const rows = [];
  for (let i = 0; i < days.length; i += 6) rows.push(days.slice(i, i + 6));
  if (rows.length > 1 && rows[rows.length - 1].length === 1) rows[rows.length - 2].push(...rows.pop());
  return rows;
};
const COMPANY = "Radiants Civiletric Pvt Ltd";

// Off-screen, fixed-style card rendered only for PNG capture (WhatsApp-friendly).
export const ActivityReportExport = forwardRef(function ActivityReportExport({ rep, projectName, logoUrl }, ref) {
  const remarks = rep.dates.filter((d) => rep.remarks?.[d]?.text);
  const th = { padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#ffffff", textAlign: "right", whiteSpace: "nowrap" };
  const td = { padding: "9px 12px", fontSize: 14, color: "#334155", textAlign: "right", borderBottom: "1px solid #e2e8f0" };
  return (
    <div ref={ref} style={{ display: "inline-block", minWidth: 720, background: "#f8fafc", padding: 32, fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      <div style={{ background: "#0f766e", borderRadius: 16, padding: "22px 28px", color: "#fff", display: "flex", alignItems: "center", gap: 24 }}>
        {logoUrl && (
          <div style={{ background: "#fff", borderRadius: 12, padding: "10px 14px", height: 84, width: 190, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <img src={logoUrl} alt="" crossOrigin="anonymous" style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
          </div>
        )}
        <div>
          <div style={{ fontSize: 13, letterSpacing: 2, textTransform: "uppercase", opacity: 0.8 }}>{COMPANY}</div>
          <div style={{ fontSize: 28, fontWeight: 800, marginTop: 6 }}>Activity Report – {projectName}</div>
          <div style={{ fontSize: 15, marginTop: 6, opacity: 0.9 }}>Period: {fmtLong(rep.period?.from)} to {fmtLong(rep.period?.to)}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 18 }}>
        <div style={{ flex: 1, background: "#fff", border: "1px solid #ccfbf1", borderRadius: 12, padding: "14px 18px" }}>
          <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Total Activity</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: "#0f766e" }}>{rep.grand_total}</div>
        </div>
        {rep.leading && (
          <div style={{ flex: 2, background: "#fff", border: "1px solid #ccfbf1", borderRadius: 12, padding: "14px 18px" }}>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Leading Activity</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#134e4a", marginTop: 4 }}>{rep.leading.name} <span style={{ color: "#0f766e" }}>· {rep.leading.total}</span></div>
          </div>
        )}
      </div>
      <div style={{ marginTop: 18, background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr style={{ background: "#134e4a" }}>
              <th style={{ ...th, textAlign: "left" }}>Activity</th>
              {rep.dates.map((d) => <th key={d} style={th}>{fmtD(d)}</th>)}
              <th style={{ ...th, background: "#0f766e" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rep.activities.filter((a) => rep.activity_totals[a.id]).map((a, i) => (
              <tr key={a.id} style={{ background: i % 2 ? "#f8fafc" : "#fff" }}>
                <td style={{ ...td, textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>{a.name}{a.uom ? ` (${a.uom})` : ""}</td>
                {rep.dates.map((d) => <td key={d} style={td}>{(rep.matrix[a.id] || {})[d] || "–"}</td>)}
                <td style={{ ...td, fontWeight: 800, color: "#0f766e" }}>{rep.activity_totals[a.id]}</td>
              </tr>
            ))}
            <tr style={{ background: "#ccfbf1" }}>
              <td style={{ ...td, textAlign: "left", fontWeight: 800, color: "#134e4a" }}>TOTAL</td>
              {rep.dates.map((d) => <td key={d} style={{ ...td, fontWeight: 800, color: "#134e4a" }}>{rep.date_totals?.[d] || "–"}</td>)}
              <td style={{ ...td, fontWeight: 800, color: "#134e4a" }}>{rep.grand_total}</td>
            </tr>
            <tr style={{ background: "#eef2ff" }}>
              <td style={{ ...td, textAlign: "left", fontWeight: 800, color: "#3730a3" }}>AVG / TEAM</td>
              {rep.dates.map((d) => <td key={d} style={{ ...td, fontWeight: 700, color: "#3730a3" }}>{rep.date_avg_per_team?.[d] ?? "–"}</td>)}
              <td style={{ ...td, fontWeight: 800, color: "#3730a3" }}>{rep.avg_per_team}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {remarks.length > 0 && (
        <div style={{ marginTop: 18, background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "16px 20px" }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#134e4a", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Day Remarks</div>
          {remarkRows(remarks).map((row, ri) => (
            <div key={ri} style={{ display: "flex", gap: 10, marginTop: ri ? 10 : 0 }}>
              {row.map((d) => (
                <div key={d} style={{ flex: "1 1 0", minWidth: 0, background: "#f0fdfa", border: "1px solid #ccfbf1", borderRadius: 10, padding: "8px 10px" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#0f766e" }}>{fmtD(d)} · {rep.date_totals?.[d] || 0}</div>
                  <div style={{ fontSize: 12, lineHeight: 1.35, color: "#334155", whiteSpace: "pre-wrap", wordBreak: "break-word", marginTop: 3 }}>{rep.remarks[d].text}</div>
                </div>
              ))}
              {row.length < 6 && Array.from({ length: 6 - row.length }).map((_, i) => <div key={`f${i}`} style={{ flex: "1 1 0" }} />)}
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 14, fontSize: 12, color: "#94a3b8", textAlign: "right" }}>Generated {new Date().toLocaleString("en-IN")} · MeterFlow</div>
    </div>
  );
});
